/**
 * Validates HTML content for SSR usage in dangerouslySetInnerHTML
 * Returns empty string if HTML is invalid or contains potentially dangerous content
 * @param htmlContent - The HTML string to validate
 * @returns Validated HTML string or empty string if invalid
 */
function validateHtmlForSSR(htmlContent: string | null | undefined): string {
  if (!htmlContent || typeof htmlContent !== "string") {
    return "";
  }

  try {
    // Performance optimization: Skip validation for very large content
    if (htmlContent.length > 10000) {
      if (/<script[\s\S]*?<\/script>/gi.test(htmlContent)) {
        return "";
      }
      return htmlContent;
    }

    // -------------------------------
    // 🚨 NEW: Strong malformed tag detection
    // Catches: <strong不到15分钟</strong>, <div中文</div>
    // -------------------------------
    const malformedOpeningTagPattern =
      /<([a-zA-Z][a-zA-Z0-9]*)(\s[^>]*)?[\u4e00-\u9fff\u3040-\u309f\u30a0-\u30ff]/g;

    if (malformedOpeningTagPattern.test(htmlContent)) {
      console.warn(
        "Malformed HTML opening tag detected in validateHtmlForSSR (missing '>')",
        htmlContent.substring(0, 120),
      );
      return "";
    }

    // -------------------------------
    // Dangerous pattern checks
    // -------------------------------
    const dangerousPatterns = [
      /<script[\s\S]*?<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /<iframe/gi,
      /<object/gi,
      /<embed/gi,
    ];

    for (const pattern of dangerousPatterns) {
      pattern.lastIndex = 0;
      if (pattern.test(htmlContent)) {
        console.warn("Dangerous HTML pattern detected in validateHtmlForSSR:", pattern);
        return "";
      }
    }

    // -------------------------------
    // Basic tag balance check
    // -------------------------------
    const openTags = (htmlContent.match(/<[^/][^>]*>/g) || []).length;
    const closeTags = (htmlContent.match(/<\/[^>]+>/g) || []).length;
    const selfClosingTags = (htmlContent.match(/<[^>]+\/>/g) || []).length;

    const selfClosingTagPattern =
      /<(br|hr|img|input|meta|link|area|base|col|embed|source|track|wbr)\s*[^>]*>/gi;

    const selfClosingMatches = (htmlContent.match(selfClosingTagPattern) || []).length;

    const adjustedOpenTags = openTags - (selfClosingMatches - selfClosingTags);

    const tagBalance = adjustedOpenTags - closeTags - selfClosingTags;

    const maxImbalance = 15;

    if (Math.abs(tagBalance) > maxImbalance) {
      if (Math.abs(tagBalance) > 30) {
        console.warn("Significant HTML tag imbalance detected in validateHtmlForSSR", {
          openTags,
          closeTags,
          selfClosingTags,
          selfClosingMatches,
          adjustedOpenTags,
          tagBalance,
          htmlPreview: htmlContent.substring(0, 200),
        });
        return "";
      }

      console.warn("Moderate HTML tag imbalance detected, but allowing:", {
        tagBalance,
        htmlPreview: htmlContent.substring(0, 100),
      });
    }

    return htmlContent;
  } catch (error) {
    console.warn("Error validating HTML in validateHtmlForSSR:", error);
    return "";
  }
}

export default validateHtmlForSSR;
